import { useEffect, useRef, useState } from 'react';

import { useLocalizedStringFormatter } from '@coursera/cds-common';

import Typography from '@core/Typography2';
import VisuallyHidden from '@core/VisuallyHidden';

import i18nMessages from './i18n';

type Props = {
  characterLimit: number;
  charactersLeft: number;
  id?: string;
  isLimitReached?: boolean;
  className?: string;
};

const CharacterLimit = (props: Props) => {
  const {
    className,
    charactersLeft,
    characterLimit,
    isLimitReached,
    id,
  } = props;

  const [announcedCharactersLeft, setAnnouncedCharactersLeft] = useState(
    charactersLeft
  );

  const isMountRef = useRef(true);
  const prevCharactersLeftRef = useRef(charactersLeft);
  // Always reflects the latest value so the debounced callback announces
  // the current count rather than the stale value at threshold-crossing time.
  const latestCharactersLeftRef = useRef(charactersLeft);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set to true once a threshold is crossed; cleared after announcement fires.
  const pendingAnnouncementRef = useRef(false);

  latestCharactersLeftRef.current = charactersLeft;

  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  useEffect(() => {
    // Skip the initial mount — announcedCharactersLeft is already initialised
    // from useState(charactersLeft) so no announcement is needed.
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    const prev = prevCharactersLeftRef.current;
    prevCharactersLeftRef.current = charactersLeft;

    const ratio = charactersLeft / characterLimit;
    const prevRatio = prev / characterLimit;

    // Detect threshold crossings rather than exact equality so fast typing
    // that skips over a threshold value still triggers an announcement.
    const crossedThreshold =
      ratio === 1 ||
      prevRatio > 0.5 !== ratio > 0.5 ||
      prevRatio > 0.1 !== ratio > 0.1 ||
      ratio <= 0;

    if (crossedThreshold) {
      pendingAnnouncementRef.current = true;
    }

    if (!pendingAnnouncementRef.current) return;

    // Debounce: aria-live="polite" defers announcements while the user is
    // actively typing. Update the live region only after typing pauses so
    // the screen reader actually gets a chance to announce.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAnnouncedCharactersLeft(latestCharactersLeftRef.current);
      pendingAnnouncementRef.current = false;
      timerRef.current = null;
    }, 1000);
  }, [characterLimit, charactersLeft]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      <VisuallyHidden id={id}>
        {stringFormatter.format('characterLimitLabel', {
          number: characterLimit,
        })}
      </VisuallyHidden>
      <Typography
        aria-hidden
        className={className}
        color={isLimitReached ? 'error' : 'supportText'}
        component="p"
        variant="bodySecondary"
      >
        {isLimitReached
          ? stringFormatter.format('characterLimitTooMany', {
              number: Math.abs(charactersLeft),
            })
          : stringFormatter.format('characterLimitRemaining', {
              number: charactersLeft,
            })}
      </Typography>
      <VisuallyHidden aria-live="polite">
        {isLimitReached
          ? stringFormatter.format('characterLimitTooManySr', {
              number: Math.abs(announcedCharactersLeft),
            })
          : stringFormatter.format('characterLimitRemainingSr', {
              number: announcedCharactersLeft,
            })}
      </VisuallyHidden>
    </>
  );
};

export default CharacterLimit;
